import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ShieldQuestion, Clock, IdCard, ScanFace, Lock, LifeBuoy, RefreshCw, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const STATUS_UI = {
  none: { Icon: IdCard, tone: "text-zinc-300", title: "Verify your age (18+)", body: "MyPandaX is for adults. A quick government-ID and selfie check confirms you're 18 or older." },
  pending: { Icon: Clock, tone: "text-amber-300", title: "Verification in progress", body: "Finish the ID + selfie steps, or resume where you left off." },
  verified: { Icon: ShieldCheck, tone: "text-emerald-300", title: "Verified 18+", body: "You're verified. This status is private — nothing is shown on your profile." },
  failed: { Icon: ShieldAlert, tone: "text-rose-300", title: "Verification failed", body: "We couldn't confirm you're 18+. You can try again with a valid ID, or ask our team for a manual review." },
  needs_review: { Icon: ShieldQuestion, tone: "text-sky-300", title: "Manual review", body: "A human on our Safety team is reviewing your verification. You'll be notified when it's done." },
  expired: { Icon: Clock, tone: "text-zinc-300", title: "Session expired", body: "Your verification session timed out. Start a fresh one whenever you're ready." },
};
const REASON = { under_18: "Our records show you are under 18.", verification_declined: "The ID or selfie check didn't pass.", age_unconfirmed: "Your ID passed but the birthdate couldn't be read.", session_expired: "The session expired.", manual_review: "Decided by the Safety team.", member_appeal: "You requested a review." };

function MockFlow({ onDone }) {
  const [step, setStep] = useState(0);
  const run = async (outcome) => {
    try {
      const { data } = await api.post("/verify/mock/complete", { outcome });
      onDone(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Simulation failed"); }
  };
  if (step === 0) return (
    <div className="rounded-2xl border border-dashed border-amber-400/40 bg-amber-500/5 p-4 space-y-3" data-testid="verify-mock-flow">
      <div className="text-[11px] uppercase tracking-[0.2em] text-amber-300">Preview simulator · Persona Sandbox not connected</div>
      <div className="flex items-center gap-3 text-sm text-zinc-200"><IdCard size={18} className="text-rose-300" /> Step 1 · Government ID (front + back)</div>
      <Button className="btn-crimson rounded-full" data-testid="verify-mock-id-next" onClick={() => setStep(1)}>I&apos;ve scanned my ID</Button>
    </div>
  );
  if (step === 1) return (
    <div className="rounded-2xl border border-dashed border-amber-400/40 bg-amber-500/5 p-4 space-y-3" data-testid="verify-mock-flow">
      <div className="flex items-center gap-3 text-sm text-zinc-200"><ScanFace size={18} className="text-rose-300" /> Step 2 · Selfie with liveness</div>
      <Button className="btn-crimson rounded-full" data-testid="verify-mock-selfie-next" onClick={() => setStep(2)}>Selfie captured</Button>
    </div>
  );
  return (
    <div className="rounded-2xl border border-dashed border-amber-400/40 bg-amber-500/5 p-4 space-y-3" data-testid="verify-mock-flow">
      <div className="text-sm text-zinc-200">Step 3 · Simulated provider result</div>
      <div className="flex flex-wrap gap-2">
        {[["adult", "Adult passes"], ["underage", "Under 18"], ["declined", "ID/selfie declined"], ["review", "Needs review"], ["expired", "Session expired"], ["abandon", "Close window (interrupt)"]].map(([k, l]) => (
          <Button key={k} variant="outline" className="rounded-full border-white/15 text-xs" data-testid={`verify-mock-${k}`} onClick={() => run(k)}>{l}</Button>
        ))}
      </div>
    </div>
  );
}

export default function VerifyAgePage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mockOpen, setMockOpen] = useState(false);
  const [appeal, setAppeal] = useState("");
  const clientRef = useRef(null);

  const load = useCallback(async () => {
    const [{ data: s }, { data: c }] = await Promise.all([api.get("/verify/status"), api.get("/verify/config")]);
    setSt(s); setCfg(c);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/verify/start");
      if (data.mode === "mock") { setMockOpen(true); await load(); await refresh(); return; }
      const Persona = (await import("persona")).default;
      clientRef.current = new Persona.Client({
        inquiryId: data.inquiry_id, sessionToken: data.session_token, environmentId: data.environment_id,
        onReady: () => clientRef.current?.open(),
        onComplete: async () => { toast.message("Submitted — waiting for the provider's decision…"); await load(); await refresh(); },
        onCancel: async () => { toast.message("Verification paused. You can resume any time."); await load(); },
        onError: () => toast.error("The verification window hit an error. Please try again."),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start verification.");
    } finally { setBusy(false); }
  };
  const onMockDone = async (data) => { setMockOpen(false); setSt((p) => ({ ...p, ...data })); await refresh(); toast.message(`Result: ${STATUS_UI[data.status]?.title || data.status}`); };
  const sendAppeal = async () => {
    try { await api.post("/verify/appeal", { message: appeal }); toast.success("Sent to our Safety team."); setAppeal(""); await load(); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send."); }
  };

  if (!st || !cfg) return <div className="p-6 text-sm text-zinc-500" data-testid="verify-loading">Loading…</div>;
  const ui = STATUS_UI[st.status] || STATUS_UI.none;
  const locked = st.underage_lock && st.status !== "verified";
  const canStart = ["none", "failed", "expired", "pending"].includes(st.status);
  const canAppeal = ["failed", "expired", "pending"].includes(st.status) && !(st.appeal && st.appeal.status === "open");

  return (
    <div className="max-w-xl mx-auto p-5 sm:p-8 space-y-6" data-testid="verify-age-page">
      {!(st.required && st.status !== "verified") && <button type="button" onClick={() => navigate(-1)} className="text-xs text-zinc-400 flex items-center gap-1 hover:text-white" data-testid="verify-back"><ArrowLeft size={12} /> Back</button>}
      {st.required && st.status !== "verified" && (
        <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-50" data-testid="verify-required-notice">
          <span className="font-semibold">Verification required.</span> MyPandaX is an 18+ community — livestreams, communities, Social Meet, messaging and gifts unlock once you&apos;re Verified 18+. Your account, coins and Bamboo are safe meanwhile.
        </div>
      )}
      {locked && (
        <div className="rounded-2xl border border-rose-500/40 bg-black/40 px-4 py-3 text-sm text-rose-100" data-testid="verify-underage-lock">
          <Lock size={14} className="inline mr-1" /> Our check found a birthdate under 18. This can&apos;t be lifted by our team — only a fresh Persona check with a valid government ID showing you are 18+ can change it.
        </div>
      )}
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center ${ui.tone}`}><ui.Icon size={24} /></div>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl tracking-tight text-white" data-testid="verify-title">{ui.title}</h1>
          <p className="text-sm text-zinc-400 mt-1" data-testid="verify-body">{ui.body}</p>
          {st.reason && st.status !== "verified" && <p className="text-xs text-zinc-500 mt-1" data-testid="verify-reason">{REASON[st.reason] || st.reason}</p>}
        </div>
      </div>
      <span className="sr-only" data-testid="verify-status">{st.status}</span>

      {st.status !== "verified" && !mockOpen && (
        <ul className="grid sm:grid-cols-3 gap-2 text-xs text-zinc-300">
          {[[IdCard, "Government ID", "Driver’s licence, passport or national ID"], [ScanFace, "Selfie check", "Quick liveness selfie matched to your ID"], [Lock, "Private by design", "Only your 18+ status is stored — never your documents"]].map(([I, t, d]) => (
            <li key={t} className="rounded-xl border border-white/10 bg-white/[0.03] p-3"><I size={16} className="text-rose-300 mb-1.5" /><div className="font-semibold text-white">{t}</div><div className="text-zinc-500 mt-0.5">{d}</div></li>
          ))}
        </ul>
      )}

      {mockOpen && <MockFlow onDone={onMockDone} />}

      <div className="flex flex-wrap gap-2">
        {canStart && !mockOpen && (
          <Button className="btn-crimson rounded-full px-6" disabled={busy} onClick={start} data-testid="verify-start-btn">
            {busy ? <RefreshCw size={14} className="animate-spin mr-2" /> : null}{st.status === "pending" ? "Resume verification" : st.status === "none" ? "Start verification" : "Try again"}
          </Button>
        )}
        {st.status === "verified" && <Button variant="outline" className="rounded-full border-white/15" onClick={() => navigate("/feed")} data-testid="verify-done-btn">Continue to MyPandaX</Button>}
      </div>

      {st.appeal?.status === "open" && <div className="rounded-xl border border-sky-400/30 bg-sky-500/10 p-3 text-xs text-sky-100" data-testid="verify-appeal-open"><LifeBuoy size={14} className="inline mr-1" /> Your review request is with the Safety team.</div>}
      {canAppeal && (
        <details className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="verify-appeal-box">
          <summary className="text-sm text-zinc-200 cursor-pointer flex items-center gap-2"><LifeBuoy size={14} className="text-sky-300" /> Can&apos;t complete verification? Ask for a human review</summary>
          <div className="mt-3 space-y-2">
            <Textarea value={appeal} onChange={(e) => setAppeal(e.target.value)} placeholder="Tell us what happened (don't include ID numbers or photos here)." className="bg-black/40 border-white/10 text-sm" data-testid="verify-appeal-text" />
            <Button variant="outline" className="rounded-full border-white/15" disabled={appeal.trim().length < 10} onClick={sendAppeal} data-testid="verify-appeal-send">Send to Safety team</Button>
          </div>
        </details>
      )}
      <p className="text-[11px] text-zinc-600">Verification is handled by Persona. MyPandaX never receives or stores your ID photos, selfie or biometric data — only the result. Status is private; no badge is shown to other members.{user?.email ? "" : ""}</p>
    </div>
  );
}
